import { guid } from '@/components/utils/core/uid';
import type { JobsModeratorsVacancy } from '@/types/graphql';
import type { RootState } from '@/types/store/index';
import type {
  ContractsDict,
  DeleteContractMutationArgs,
  EditableContract,
  GenerateContractsMutationArgs,
  SetContractsFormTouchedMutationArgs,
  SetContractVacancyMutationArgs,
  State,
  Steps,
  SubmissionStates,
  UpdateContractMutationArgs,
} from '@/types/store/jobs-employer-create-update-contracts';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';

export const state = (): State => ({
  contracts: {},
  step: 'intro',
  contractsFormTouched: false,
  contractsFormUpdated: false,
  vacanciesFormUpdated: false,
  submissionState: 'idle',
});

export const mutations: MutationTree<State> = {
  setStep(state, step: Steps): void {
    state.step = step;
  },
  generateContracts(
    state,
    { contracts = [], activeContracts = [], vacancies = [] }: GenerateContractsMutationArgs,
  ): void {
    const activeContractIds = activeContracts.map(contract => contract.id);
    const activeContractsVacancies = vacancies.reduce((acc: JobsModeratorsVacancy[], vacancy) => {
      if (!vacancy.contract?.id) return acc;
      if (!activeContractIds.includes(vacancy.contract.id)) return acc;

      acc.push(vacancy);
      return acc;
    }, []);

    state.contracts = contracts.reduce((acc: ContractsDict, contract) => {
      const vacancyIds = activeContractsVacancies.reduce((acc: string[], vacancy) => {
        if (vacancy.contract?.id !== contract.id) return acc;

        acc.push(vacancy.id);
        return acc;
      }, []);

      acc[contract.id] = {
        ...contract,
        vacancyIds,
        linkedVacancyCount: vacancyIds.length,
        isValid: true,
      };

      return acc;
    }, {});
  },
  addContract(state): void {
    const id = guid();
    const newContract: EditableContract = {
      id,
      contractType: null,
      vacancyIds: [],
      startDate: null,
      endDate: null,
      linkedVacancyCount: 0,
      isValid: false,
      isNew: true,
    };

    state.contracts = { ...state.contracts, [id]: newContract };
  },
  updateContract(state, contract: UpdateContractMutationArgs) {
    state.contracts[contract.id] = { ...contract };
  },
  setContractVacancy(state, { contractId, vacancyId }: SetContractVacancyMutationArgs) {
    const currentContract = Object.values(state.contracts).find(contract => contract.vacancyIds?.includes(vacancyId));

    if (currentContract) {
      const vacancyIdsForCurrentContract = (currentContract.vacancyIds || []).filter(id => id !== vacancyId);
      state.contracts[currentContract.id] = { ...currentContract, vacancyIds: vacancyIdsForCurrentContract };
    }

    const contract = state.contracts[contractId];
    state.contracts[contractId] = { ...contract, vacancyIds: [...(contract.vacancyIds || []), vacancyId] };
  },
  deleteContract(state, contractId: DeleteContractMutationArgs): void {
    const { [contractId]: _, ...contracts } = state.contracts;
    state.contracts = contracts;
  },
  setContractsFormTouched(state, touched: SetContractsFormTouchedMutationArgs): void {
    state.contractsFormTouched = touched;
  },
  setContractsFormUpdated(state, updated: SetContractsFormTouchedMutationArgs): void {
    state.contractsFormUpdated = updated;
  },
  setVacanciesFormUpdated(state, updated: SetContractsFormTouchedMutationArgs): void {
    state.vacanciesFormUpdated = updated;
  },
  setSubmissionState(state, submissionState: SubmissionStates): void {
    state.submissionState = submissionState;
  },
};

export const getters: GetterTree<State, RootState> = {
  isContractsValid(state): boolean {
    return !Object.values(state.contracts).some(contract => !contract.isValid);
  },
};

export const actions: ActionTree<State, RootState> = {
  moveToNextStep({ state, commit }) {
    switch (state.step) {
      case 'intro': {
        commit('setStep', 'contracts');
        return;
      }
      case 'contracts': {
        commit('setStep', 'vacancies');
      }
    }
  },
};
